// API基础服务

// 通用请求配置
const API_BASE_URL = '/api';

// 通用错误处理
class ApiError extends Error {
  public status: number;
  public data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// 通用请求函数
async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const fullUrl = `${API_BASE_URL}${url}`;

  // 设置默认请求头
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // 添加认证令牌（如果存在）
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `请求失败: ${response.status}`,
        response.status,
        errorData
      );
    }

    // 处理204 No Content响应
    if (response.status === 204) {
      return {} as T;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    } else {
      throw new ApiError(
        error instanceof Error ? error.message : '网络请求失败',
        0
      );
    }
  }
}

// GET请求
export async function get<T>(url: string, params?: Record<string, any>): Promise<T> {
  let queryString = '';
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const paramsString = searchParams.toString();
    if (paramsString) {
      queryString = `?${paramsString}`;
    }
  }
  return request<T>(`${url}${queryString}`, { method: 'GET' });
}

// POST请求
export async function post<T>(url: string, data?: any): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// PUT请求
export async function put<T>(url: string, data?: any): Promise<T> {
  return request<T>(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// DELETE请求
export async function del<T>(url: string): Promise<T> {
  return request<T>(url, { method: 'DELETE' });
}

export { ApiError };
